import {
  type ClearSettingRequest,
  type SettingDetailDto,
  type SettingsDetailsQuerySchema,
  type SettingsDetailsResponse,
  type SettingsRegistryMetadata,
  type WriteSettingRequest,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/**
 * What the screen may narrow a details request by — the contract's own input
 * shape (U3), never a hand-written copy of it.
 *
 * `z.input`, because `keys` travels as one comma-joined value and the schema
 * parses it into an array on the server (N7) — the same spelling every other
 * admin api uses for an `ids` filter.
 */
type SettingsDetailsParams = z.input<typeof SettingsDetailsQuerySchema>;

/**
 * The settings surface (W8) — **shared**, not module-owned.
 *
 * It sat in `modules/Settings/services/` until P10.5d2, and moved because P10.4
 * gave a definition the right to name **another module** as the owner of its
 * editor (`managedBy`): the Integrations panel now reads and writes
 * `ai.pricing`, and W6 forbids it importing `modules/Settings`. The alternative
 * — a second `setting-lookup.api` with its own cache, cross-invalidated through
 * the `settings.value.changed` event — was rejected: two caches of one resource
 * is exactly the drift W5/W6 exist to prevent, and writing that event's first
 * subscriber to paper over a duplicate we chose to create is backwards. With one
 * api, a write from Integrations invalidates the `Setting` tag the Settings
 * screen reads, for free. Every later owning phase (P14 · P21 · P23/P26) lands
 * on the same seam.
 *
 * `registry` is the whole screen: groups, subgroup sections, controls, scopes and
 * coded defaults arrive in one request, and a definition added to the api appears
 * here with **no admin code at all**. It is deliberately cached under its own tag
 * and never invalidated by a value write — the registry is code, and only a
 * deployment changes it.
 *
 * `settingsDetails` is the other half: for every key it names, the rows that have
 * actually been stored **and** what each allowed scope currently RESOLVES to.
 * It is per GROUP rather than per key — the screen renders a group at a time,
 * and one request per row on screen was twelve requests to open `brand`. Each
 * item provides its own `Setting` tag, so the write and clear mutations below
 * refresh the batch with no invalidation code of their own.
 */
export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['SettingsRegistry', 'Setting'],
  endpoints: (builder) => ({
    settingsRegistry: builder.query<SettingsRegistryMetadata, void>({
      query: () => ({ url: '/admin/settings/registry', method: 'GET' }),
      providesTags: ['SettingsRegistry'],
    }),

    settingsDetails: builder.query<SettingsDetailsResponse, SettingsDetailsParams>({
      query: (params) => ({ url: '/admin/settings/details', method: 'GET', params }),
      providesTags: (result) =>
        (result?.items ?? []).map((item) => ({ type: 'Setting' as const, id: item.key })),
    }),

    writeSetting: builder.mutation<SettingDetailDto, { key: string; body: WriteSettingRequest }>({
      query: ({ key, body }) => ({ url: `/admin/settings/${key}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { key }) => [{ type: 'Setting' as const, id: key }],
      async onQueryStarted({ key }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          EVENT_BUS.publish({ type: 'settings.value.changed', key });
        } catch {
          // Refused — nothing to announce. Swallowed deliberately: `onQueryStarted`
          // has no caller, so a rejection escaping it becomes an unhandled
          // rejection. The failure is already the caller's, through `unwrap()`.
        }
      },
    }),

    /**
     * Clears one scope's override so inheritance visibly returns.
     *
     * A body-carrying DELETE, mirroring the api: the thing being deleted is a
     * *scope of* the key in the path, and encoding the scope in a query string
     * would make one request expressible two ways.
     */
    clearSetting: builder.mutation<SettingDetailDto, { key: string; body: ClearSettingRequest }>({
      query: ({ key, body }) => ({ url: `/admin/settings/${key}`, method: 'DELETE', body }),
      invalidatesTags: (_r, _e, { key }) => [{ type: 'Setting' as const, id: key }],
      async onQueryStarted({ key }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          EVENT_BUS.publish({ type: 'settings.value.changed', key });
        } catch {
          // See `writeSetting`.
        }
      },
    }),
  }),
});

// `listSettings` (the stored-values audit view) and `settingDetail` (one key)
// were removed with the batch read: neither had a consumer left, and a hook
// nothing calls is a lie in the inventory — the test P10.1 retired `ScrollArea`
// on. Both api ROUTES stand; `GET /admin/settings/details` is built on the
// single-key resolver server-side, and re-adding a hook is three lines.
export const {
  useSettingsRegistryQuery,
  useSettingsDetailsQuery,
  useWriteSettingMutation,
  useClearSettingMutation,
} = settingsApi;
